import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthWidgetComponent } from '../widgets/auth/auth-widget.component';
import { I18nService } from '../core/i18n.service';
import { AuthService, AuthView } from '../core/auth.service';
import { SiteConfigService } from '../core/site-config.service';

@Component({
  selector: 'app-auth-page',
  imports: [AuthWidgetComponent],
  template: `
    @if (isSso()) {
      <main class="sso-auth">
        <section class="sso-auth__access">
          <header class="partner-brand">
            @if (partner().logoUrl) { <img [src]="partner().logoUrl" [alt]="partner().name" /> }
            <div><span>GÜVENLİ HESAP GİRİŞİ</span><strong>{{ partner().name }} için giriş yapın veya kayıt olun</strong></div>
          </header>
          <div class="sso-auth__form">
            @if (auth.loggedIn() && !chooseAnotherAccount()) {
              <section class="account-chooser" aria-labelledby="account-chooser-title">
                <p class="eyebrow">AYMUDO HESABI</p>
                <h2 id="account-chooser-title">Nasıl devam etmek istersiniz?</h2>
                <p>Fal Deryası’na hangi Aymudo hesabıyla giriş yapılacağını siz seçin.</p>
                <button class="current-account" type="button" (click)="continueCurrent()" [disabled]="continuing()">
                  <span class="account-avatar">{{ currentInitials() }}</span>
                  <span><strong>{{ currentName() }}</strong><small>{{ auth.user()?.email }}</small></span>
                  <b>{{ continuing() ? 'Bağlanıyor…' : 'Devam et' }} →</b>
                </button>
                <div class="chooser-actions">
                  <button type="button" (click)="showDifferentAccount()">Farklı hesapla giriş yap</button>
                  <button type="button" (click)="showNewAccount()">Yeni hesap oluştur</button>
                </div>
                @if (otherAccounts().length) {
                  <div class="remembered-accounts">
                    <span>Bu cihazda daha önce kullanılan hesaplar</span>
                    @for (account of otherAccounts(); track account.email) {
                      <button type="button" (click)="selectRememberedAccount(account)"><span><strong>{{ account.name }}</strong><small>{{ account.email }}</small></span><b>Seç →</b></button>
                    }
                  </div>
                }
                @if (auth.error()) { <p class="chooser-error">{{ auth.error() }}</p> }
              </section>
            } @else {
              <app-auth-widget [initialView]="selectedView()" [prefillEmail]="prefillEmail()" [autoOpen]="true" [inline]="true" [showLauncher]="false" [showSession]="false" />
            }
          </div>
          <button class="partner-return" type="button" (click)="returnToPartner()">← {{ partner().name }}’na geri dön</button>
          <small class="sso-provider">Kimlik doğrulama Aymudo hesabı üzerinden güvenli biçimde tamamlanır.</small>
        </section>
      </main>
    } @else {
      <section class="page-card auth-page">
        <p class="eyebrow">{{ i18n.t('login') }} / {{ i18n.t('register') }}</p>
        <h1>{{ title }}</h1><p>{{ intro() }}</p>
        <app-auth-widget [initialView]="initialView" [autoOpen]="site.config().auth.mode !== 'aymudo-sso'" />
      </section>
    }
  `,
  styleUrl: './auth.page.scss'
})
export class AuthPage {
  private readonly route = inject(ActivatedRoute);
  protected readonly i18n = inject(I18nService);
  protected readonly auth = inject(AuthService);
  protected readonly site = inject(SiteConfigService);
  private readonly routePath = this.route.snapshot.routeConfig?.path ?? '';
  protected readonly initialView = /kayit|register|abone-ol|subscribe/i.test(this.routePath) ? 'register' : 'login';
  protected readonly selectedView = signal<AuthView>(this.initialView);
  protected readonly chooseAnotherAccount = signal(false);
  protected readonly prefillEmail = signal('');
  protected readonly continuing = signal(false);
  protected readonly title = this.initialView === 'register' ? this.i18n.t('register') : this.i18n.t('login');
  protected readonly isSso = computed(() => this.route.snapshot.queryParamMap.get('sso') === '1');
  protected readonly partner = computed(() => this.auth.ssoContext() ?? {name:'Bağlı site',logoUrl:'',returnUri:'',host:''});
  protected readonly currentName=computed(()=>{const user=this.auth.user();return [user?.name,user?.surname].filter(Boolean).join(' ').trim()||user?.email||'Aymudo hesabı'});
  protected readonly currentInitials=computed(()=>this.currentName().split(/\s+/).slice(0,2).map(part=>part.charAt(0)).join('').toUpperCase()||'AY');
  protected readonly otherAccounts=computed(()=>{const server=this.auth.ssoAccounts().filter(item=>!item.active);if(server.length)return server;const current=String(this.auth.user()?.email||'').toLowerCase();return this.auth.rememberedAccounts().filter(item=>item.email!==current).map(item=>({accountID:0,name:item.name,email:item.email,active:false}))});

  constructor(){
    effect(()=>{if(this.auth.loggedIn())this.continueRequestedRedirect()});
    void this.initializeSso();
  }

  private continueRequestedRedirect():void{
    if(typeof window==='undefined')return;
    const requested=this.route.snapshot.queryParamMap.get('redirect_uri')||'';
    if(!requested)return;
    try{
      const target=new URL(requested,window.location.origin);
      if(target.origin===window.location.origin && target.pathname==='/oauth/authorize')window.location.assign(target.toString());
    }catch{}
  }

  private async initializeSso():Promise<void>{
    await this.auth.refresh(true);
    if(this.isSso())await Promise.all([this.auth.loadPendingSsoContext(),this.auth.loadPendingSsoAccounts()]);
  }

  protected returnToPartner():void{
    if(typeof window==='undefined')return;
    if(window.opener&&!window.opener.closed){window.close();return;}
    const requested=this.partner().returnUri;
    if(!requested){window.history.back();return;}
    try{const target=new URL(requested);target.searchParams.delete('aymudo_auth_popup');window.location.assign(target.toString());}
    catch{window.history.back();}
  }

  protected async continueCurrent():Promise<void>{
    if(this.continuing())return;
    this.continuing.set(true);
    const redirect=await this.auth.continuePendingSso();
    this.continuing.set(false);
    if(redirect&&typeof window!=='undefined'&&!this.auth.completeSsoWindow(redirect))window.location.assign(redirect);
  }

  protected async showDifferentAccount(email=''):Promise<void>{
    if(!(await this.auth.preparePendingSsoAccountChange()))return;
    this.prefillEmail.set(email);
    this.selectedView.set('login');
    this.chooseAnotherAccount.set(true);
  }

  protected async showNewAccount():Promise<void>{
    if(!(await this.auth.preparePendingSsoAccountChange()))return;
    this.prefillEmail.set('');
    this.selectedView.set('register');
    this.chooseAnotherAccount.set(true);
  }

  protected async selectRememberedAccount(account:{accountID:number;email:string|null}):Promise<void>{
    if(account.accountID>0){this.continuing.set(true);const redirect=await this.auth.switchPendingSsoAccount(account.accountID);this.continuing.set(false);if(redirect&&typeof window!=='undefined'&&!this.auth.completeSsoWindow(redirect))window.location.assign(redirect);return;}
    await this.showDifferentAccount(account.email||'');
  }
  protected readonly intro = () => ({
    tr: 'Hesabına güvenli biçimde eriş veya yeni bir hesap oluştur.',
    en: 'Access your account securely or create a new account.',
    de: 'Greife sicher auf dein Konto zu oder erstelle ein neues Konto.',
    es: 'Accede a tu cuenta de forma segura o crea una cuenta nueva.',
    fr: 'Accédez à votre compte en toute sécurité ou créez un nouveau compte.'
  }[this.i18n.lang()]);
}
